/* eslint-disable @typescript-eslint/no-misused-spread */
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { selectInputs } from '../redux/inputsReducer';
import { selectDealerAndVehicleData } from '../redux/dealerAndVehicleReducer';
import { selectAppConfig } from '../redux/appConfigReducer';
import { ELSP_URL } from '../constants/environment';
import { prepareDealerIdForFormsApi } from '../functions/dealerIdConverter';
import { US_MARKET, FEATURE_APP_DETAILS } from '../constants';
import { reversedPhoneNumberFormatter } from '../functions/phoneNumberFormatter';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import useSourceOfLead from './useSourceOfLead';
import { digitalLeadsFormSubmissionError } from '../utils/analyticsEventListeners';
import { selectForm } from '../redux/formReducer';
import formTypeFormatter from '../functions/formTypeFormatter';
import {
  getOffersDataToSubmit,
  getOffersVehicleDataToSubmit,
} from '../functions/getOffersDataToSubmit';
import { getVehicleDataToSubmit } from '../functions/getVehicleDataToSubmit';
import consentProcessor from '../functions/consentProcessor';
import getTestDriveDataToSubmit from '../functions/getTestDriveDataToSubmit';
import getHiddenInputs from '../functions/getHiddenInputs';
import { getOptionsByCompositeCodes } from '../utils/vehicleFormattor';

export const useElspFormSubmission = () => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    zipCode,
    preferredContactMethod,
    preferredContactTime,
    ccpa,
    tcpaConsent,
    secondTcpaConsent,
    universalConsent,
    dealerConsent,
    oemConsent,
    topicOfInterest,
    tradeInVehicleYear,
    tradeInVehicleMake,
    tradeInVehicleModel,
    tradeInVehicleMileage,
    tradeInVehicleVin,
    datePicker,
    preferedBookingTime,
    modelLineOfInterest,
    modelLinesSelected,
    modelLineSelectCustom,
    purchaseTimeFrame,
    preferredDealer,
    timingHandraiser,
  } = useSelector(selectInputs);

  const { vehicle, vehicleByAudiCode, vehicleByCarline } = useSelector(selectDealerAndVehicleData);
  const {
    dealerId,
    market,
    adobeCloudId,
    ctaInnerText,
    campaignId,
    offers,
    requestType,
    fym,
    presetForm,
    handraiser,
    derivativeCode,
    formTitle,
    modelSelect,
  } = useSelector(selectAppConfig);
  const contactForm = useSelector(selectForm);

  const isUSMarket = market === US_MARKET || market?.toLowerCase().includes('us');
  const isAfterSales = topicOfInterest === 'service' || topicOfInterest === 'parts';

  const { url, specificSource, requestSource } = useSourceOfLead(topicOfInterest, presetForm);

  const promotion = convertToPromotionsType(offers!);

  const finalDealerId = dealerId || vehicle?.dealer?.id;
  const audiCodeFYM = fym?.yourBuild?.audiCode;
  const isVehicleInfoAvailable = !!vehicle;

  const isTier3 = requestSource === 'tier_3';
  const providerRequestSource =
    isTier3 && (requestType === 'dealerGeneric' || requestType === 'dealer_generic')
      ? 'Event'
      : requestSource;
  const isTrandeIn = presetForm === 'tradeIn';
  const isBookATestDrive = presetForm === 'testDrive';

  // ---------------------- Dealer/ZIP Info to submit -----------------------//

  const dealerToSubmit = finalDealerId && prepareDealerIdForFormsApi(finalDealerId);
  const correctZip =
    isUSMarket && zipCode.length > 5
      ? zipCode
      : zipCode?.toUpperCase().replace(/(\w{3})(\w{3})/, '$1 $2');

  // ------------------------- Consent Processor --------------------------//

  const combinedConsents = {
    universal: universalConsent,
    ccpa,
    tcpa: tcpaConsent,
    secondTcpa: secondTcpaConsent,
    dealer: dealerConsent,
    oem: oemConsent,
  };
  const consents = consentProcessor(isTier3, isUSMarket, combinedConsents);

  // ---------------------- Vehicle Info to submit -----------------------//
  const vehicleToSubmit = getVehicleDataToSubmit({
    vehicle,
    vehicleByAudiCode,
    vehicleByCarline,
    audiCodeFYM,
    isTier3,
    vehicleOfInterest: modelLineOfInterest,
  });

  // ---------------------- Offer Details to submit -----------------------//

  const offerDataToSubmit = getOffersDataToSubmit(offers);

  const offerVehicleDataToSubmit = getOffersVehicleDataToSubmit(offers);

  // ---------------------- Trade In Info to submit -----------------------//

  const tradeInInfo = useMemo(() => {
    return {
      tradeInVehicleYear: parseInt(tradeInVehicleYear, 10),
      tradeInVehicleMake,
      tradeInVehicleModel,
      tradeInVehicleMileage: parseInt(tradeInVehicleMileage, 10),
      ...(tradeInVehicleVin ? { tradeInVehicleVin } : {}),
    };
  }, [
    tradeInVehicleMake,
    tradeInVehicleMileage,
    tradeInVehicleModel,
    tradeInVehicleVin,
    tradeInVehicleYear,
  ]);

  // ------------- Book a Test Drive date and time to submit ---------------//

  const testDriveDateAndTime = getTestDriveDataToSubmit(datePicker, preferedBookingTime);

  // ---------------------- Hidden Inputs -----------------------//
  // This is just for one dealer group
  const timestampAndGa = getHiddenInputs();

  const vehiclesPayload = (() => {
    // PRIORITY 1: Check if the user selected models via checkboxes
    if (modelLinesSelected && modelLinesSelected?.length > 0) {
      return modelLinesSelected.map((code) => ({
        modelDerivatID: code,
      }));
    }

    // PRIORITY 2: Check if the user selected a single dropdown model
    if (modelLineSelectCustom?.length > 0) {
      const options = getOptionsByCompositeCodes(modelSelect || []) || [];
      const modelCodeToSubmit = options?.find(
        (toCheck) => toCheck.label === modelLineSelectCustom,
      )?.value;
      return [{ modelDerivatID: modelCodeToSubmit }];
    }

    // PRIORITY 3: Check if there are campaign codes (derivativeCode) from config
    if (derivativeCode && derivativeCode?.length > 0) {
      return derivativeCode.map((code) => ({
        modelDerivatID: code,
      }));
    }

    // FALLBACK: Default generic code
    return [{ modelDerivatID: 'AUA000_AUA000' }];
  })();
  // ------------------------ Full submition data --------------------------//
  const submissionData = useMemo(() => {
    if (!handraiser) {
      return {
        additionalInfo: {
          device: 'Desktop',
        },
        comments: timestampAndGa,
        provider: {
          language: 'EN',
          requestSource: providerRequestSource,
          url,
          requestCategory: 'LEAD', // hardcoded
          requestType: formTypeFormatter(
            isVehicleInfoAvailable,
            specificSource,
            topicOfInterest,
            requestType,
          ),
          adobeCloudID: adobeCloudId || '',
          eventName: ctaInnerText || '', // ctaVerbiage in the past
          campaignId: campaignId || '',
        },
        customer: {
          firstName,
          lastName,
          preferredContactMethod,
          preferredContactTime,
          email,
          phone: phoneNumber ? reversedPhoneNumberFormatter(phoneNumber) : '',
          address: {
            zip: correctZip,
          },
        },
        consents: consents || [],
        originCountry: isUSMarket ? 'US' : 'CA',
        brand: 'AU', // hardcoded
        links: [
          {
            name: 'Lead Details',
            url,
          },
        ],
        ...(preferredDealer
          ? {
              dealer: {
                dealerId: preferredDealer,
              },
            }
          : {}),
        ...(purchaseTimeFrame ? { timeToMarket: purchaseTimeFrame } : {}),
        ...(offerDataToSubmit ? { finance: offerDataToSubmit } : {}),
        ...(vehicleToSubmit ? { vehicles: [vehicleToSubmit] } : []),
        ...(offerVehicleDataToSubmit ? { vehicles: [offerVehicleDataToSubmit] } : []),
        ...(dealerToSubmit ? { dealer: { dealerId: dealerToSubmit } } : {}),
        ...(isTrandeIn ? { tradeInInfo } : {}),
        ...(isBookATestDrive ? { appointments: testDriveDateAndTime } : {}),
      };
    }

    const attributes = [];
    if (vehicleByAudiCode) {
      const webConfigCode = { fieldName: 'WebConfigCode', value: vehicleByAudiCode.audiCode };
      attributes.push(webConfigCode);

      const carlineName = {
        fieldName: 'CarlineName',
        value: vehicleByAudiCode?.titleText || 'QATestCarline',
      };
      attributes.push(carlineName);

      const modelName = {
        fieldName: 'Modelname',
        value: vehicleByAudiCode?.subtitleText || 'QATestTrimline',
      };
      attributes.push(modelName);

      const vehicleimageurl = {
        fieldName: 'Vehicleimageurl',
        value: vehicleByAudiCode.images[0]?.url || 'QATestImageUrl',
      };
      attributes.push(vehicleimageurl);
    }

    // handraiser payload
    return {
      consents: consents || [],
      originCountry: isUSMarket ? 'US' : 'CA',
      brand: 'AU',
      requestType: formTitle,
      requestSource: 'Desktop',
      campaign: {
        id: campaignId || '',
      },
      customer: {
        firstName,
        lastName,
        contact: {
          preferredContactMethod,
          email,
          phone: phoneNumber ? reversedPhoneNumberFormatter(phoneNumber) : '',
        },
        address: {
          zip: correctZip,
        },
      },
      vehicles: vehiclesPayload,
      ...(attributes.length > 0 && { attributes }),
      ...(isUSMarket ? { timing: timingHandraiser } : {}),
    };
  }, [
    handraiser,
    consents,
    isUSMarket,
    formTitle,
    campaignId,
    firstName,
    lastName,
    preferredContactMethod,
    email,
    phoneNumber,
    correctZip,
    vehiclesPayload,
    timestampAndGa,
    providerRequestSource,
    url,
    isVehicleInfoAvailable,
    specificSource,
    topicOfInterest,
    requestType,
    adobeCloudId,
    ctaInnerText,
    preferredContactTime,
    offerDataToSubmit,
    vehicleToSubmit,
    offerVehicleDataToSubmit,
    dealerToSubmit,
    isTrandeIn,
    tradeInInfo,
    isBookATestDrive,
    testDriveDateAndTime,
    vehicleByAudiCode,
  ]);

  interface AxiosLikeError extends Error {
    response?: {
      status: number;
      data: any;
    };
  }

  const ELSPGateway = useMemo(() => {
    if (handraiser) return `${ELSP_URL}/v1/handraiser`;
    if (isAfterSales) return `${ELSP_URL}/v1/aftersales/leads`;
    return `${ELSP_URL}/v2/leads`;
  }, [isAfterSales, handraiser]);

  const handleSubmit = async () => {
    try {
      const response = await fetch(ELSPGateway, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.text().catch(() => '');
        const error: AxiosLikeError = new Error(`HTTP error! status: ${response.status}`);
        error.response = {
          status: response.status,
          data: errorData,
        };
        throw error;
      }

      const data = await response.json();
      return data;
      // eslint-disable-next-line
    } catch (error: any) {
      console.log(error);
      const errorPayload = {
        error_message: error.response?.data,
        error_code: error.response?.status,
      };
      digitalLeadsFormSubmissionError(
        contactForm,
        vehicle!,
        FEATURE_APP_DETAILS,
        promotion,
        errorPayload,
      );
      return error;
    }
  };

  return handleSubmit;
};
